import { useLocation, useNavigate } from 'react-router-dom';
import { RightPointingTailArrowIcon } from '@/assets/icons/icons';
import type {
  HelpCenterAnswerSource,
  InsightHelpSourceProps,
} from '../../types';
import styles from '../../AppHelpCenter.module.scss';
import { useHelpEvent } from '../../hooks';

// The framed version; only mounted for help-center answers, so a normal
// answer never touches the router.
const HelpSourceFrame = ({
  helpSource,
  children,
}: {
  helpSource: HelpCenterAnswerSource;
  children: InsightHelpSourceProps['children'];
}) => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const trackEvent = useHelpEvent();

  // The shared-link param opens Help on the article; Help opening closes
  // Insight Chat (only one is open at a time). The state marks it a hand-off.
  const openInHelp = (id: string) => {
    trackEvent({ event: 'insight_open_in_help', props: { article_id: id } });
    const params = new URLSearchParams(search);
    params.set('help', `article:${id}`);
    const _ = navigate(
      { pathname, search: `?${params}`, hash },
      { state: { helpSource: 'hand-off' } }
    );
  };

  return (
    <div className={styles.insightHelpSource}>
      {helpSource.label && (
        <span className={styles.insightHelpSourceLabel}>
          {helpSource.label}
        </span>
      )}
      {children}
      <div className={styles.insightHelpSourceLinks}>
        {helpSource.articles.map((article) => (
          <button
            key={article.id}
            type='button'
            className={styles.insightHelpSourceLink}
            onClick={() => openInHelp(article.id)}
          >
            <RightPointingTailArrowIcon size={14} color='var(--light-blue)' />
            <span>Open in Help: {article.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Wraps an Insight Chat answer (PRD): help-center answers get the label, a 3px
// blue rule and "Open in Help" links; any other answer renders unchanged.
const InsightHelpSource = ({
  helpSource,
  children,
}: InsightHelpSourceProps) => {
  return helpSource ? (
    <HelpSourceFrame helpSource={helpSource}>{children}</HelpSourceFrame>
  ) : (
    <>{children}</>
  );
};

export default InsightHelpSource;
